import { type FormEvent, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useUISubscribersCreateSubscriber } from '../../api/subscribers-ui/subscribers-ui.ts';

type AddSubscriberFormProps = {
  onSuccess: (username: string) => void;
  onClose: () => void;
};

export const AddSubscriberForm = ({ onSuccess, onClose }: AddSubscriberFormProps) => {
  const [newUsername, setNewUsername] = useState('');
  const [newUsoc, setNewUsoc] = useState('');
  const [formError, setFormError] = useState('');

  const { mutate: createSubscriber, isPending: isCreating } = useUISubscribersCreateSubscriber();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!newUsername.trim() || !newUsoc.trim()) {
      setFormError('All fields are required');
      return;
    }

    createSubscriber(
      { data: { username: newUsername.trim(), USOC: newUsoc.trim() } },
      {
        onSuccess: () => {
          onSuccess(newUsername.trim());
        },
        onError: (error) => {
          if (error.response?.data?.detail) {
            setFormError(error.response.data.detail);
          } else {
            setFormError('Failed to create subscriber. Please try again.');
          }
        },
      },
    );
  };

  const handleClose = () => {
    setNewUsername('');
    setNewUsoc('');
    setFormError('');
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Add New Subscriber</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            required
            fullWidth
            label="Username"
            placeholder="Enter subscriber username"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            helperText="Subscription_id"
            slotProps={{ htmlInput: { maxLength: 64 } }}
          />
          <TextField
            required
            fullWidth
            label="USOC"
            placeholder="Enter USOC code"
            value={newUsoc}
            onChange={(e) => setNewUsoc(e.target.value)}
            helperText="USOC code that maps to a speed profile"
            slotProps={{ htmlInput: { maxLength: 32 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={isCreating}>
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </form>
  );
};
